import type { TemplateData } from "../../types/types.js"

export function globalsCssTemplate(_data: TemplateData): string {
  return `@import 'tailwindcss';

/* 
 * Tailwind CSS v4 Configuration
 * Using CSS variables and @theme directive
 */

@theme {
  /* Typography */
  --font-sans: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji';
  --font-serif: Georgia, Cambria, 'Times New Roman', Times, serif;
  --font-mono: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, 'DejaVu Sans Mono', monospace;

  /* Colors - Modern palette */
  --color-background: #ffffff;
  --color-foreground: #0a0a0a;
  --color-muted: #6b7280;
  --color-border: #e5e7eb;
  
  /* Primary colors */
  --color-primary-50: #eff6ff;
  --color-primary-100: #dbeafe;
  --color-primary-200: #bfdbfe;
  --color-primary-300: #93c5fd;
  --color-primary-400: #60a5fa;
  --color-primary-500: #3b82f6;
  --color-primary-600: #2563eb;
  --color-primary-700: #1d4ed8;
  --color-primary-800: #1e40af;
  --color-primary-900: #1e3a8a;
  --color-primary-950: #172554;

  /* Secondary colors */
  --color-secondary-50: #f8fafc;
  --color-secondary-100: #f1f5f9;
  --color-secondary-200: #e2e8f0;
  --color-secondary-300: #cbd5e1;
  --color-secondary-400: #94a3b8;
  --color-secondary-500: #64748b;
  --color-secondary-600: #475569;
  --color-secondary-700: #334155;
  --color-secondary-800: #1e293b;
  --color-secondary-900: #0f172a;
  --color-secondary-950: #020617;

  /* Accent colors */
  --color-accent: #8b5cf6;
  --color-success: #10b981;
  --color-warning: #f59e0b;
  --color-error: #ef4444;
  --color-info: #3b82f6;

  /* Spacing */
  --spacing-xs: 0.5rem;
  --spacing-sm: 0.75rem;
  --spacing-md: 1rem;
  --spacing-lg: 1.5rem;
  --spacing-xl: 2rem;
  --spacing-2xl: 3rem;
  --spacing-3xl: 4rem;

  /* Border radius */
  --radius-sm: 0.125rem;
  --radius-md: 0.375rem;
  --radius-lg: 0.5rem;
  --radius-xl: 0.75rem;
  --radius-2xl: 1rem;
  --radius-full: 9999px;

  /* Shadows */
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1);

  /* Animation */
  --animate-fast: 150ms;
  --animate-normal: 300ms;
  --animate-slow: 500ms;
}

/* Dark mode theme */
@media (prefers-color-scheme: dark) {
  @theme {
    --color-background: #0a0a0a;
    --color-foreground: #fafafa;
    --color-muted: #a1a1aa;
    --color-border: #27272a;
  }
}

/* Base styles */
@layer base {
  :root {
    color-scheme: light dark;
  }

  * {
    @apply border-border;
  }

  html {
    @apply antialiased;
    font-family: var(--font-sans);
  }

  body {
    @apply bg-background text-foreground;
    font-feature-settings: "rlig" 1, "calt" 1;
  }

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    @apply font-bold tracking-tight;
  }

  h1 {
    @apply text-4xl md:text-5xl;
  }

  h2 {
    @apply text-3xl md:text-4xl;
  }

  h3 {
    @apply text-2xl md:text-3xl;
  }

  h4 {
    @apply text-xl md:text-2xl;
  }

  h5 {
    @apply text-lg md:text-xl;
  }

  h6 {
    @apply text-base md:text-lg;
  }

  /* Links */
  a {
    @apply text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300;
    @apply transition-colors duration-[var(--animate-fast)];
  }

  /* Code blocks */
  pre, code {
    @apply font-mono text-sm;
  }

  pre {
    @apply bg-secondary-100 dark:bg-secondary-900 p-4 rounded-lg overflow-x-auto;
  }

  code {
    @apply bg-secondary-100 dark:bg-secondary-900 px-1 py-0.5 rounded;
  }

  pre code {
    @apply bg-transparent p-0;
  }

  /* Forms */
  input, textarea, select {
    @apply w-full px-3 py-2 border rounded-md;
    @apply bg-background;
    @apply focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent;
    @apply transition-shadow duration-[var(--animate-fast)];
  }

  button {
    @apply px-4 py-2 font-medium rounded-md;
    @apply transition-all duration-[var(--animate-fast)];
    @apply focus:outline-none focus:ring-2 focus:ring-offset-2;
  }
}

/* Utility classes */
@layer utilities {
  /* Hide scrollbar but keep functionality */
  .scrollbar-none {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .scrollbar-none::-webkit-scrollbar {
    display: none;
  }

  /* Gradient text */
  .gradient-text {
    @apply bg-gradient-to-r from-primary-600 to-accent bg-clip-text text-transparent;
  }

  /* Glass effect */
  .glass {
    @apply bg-white/80 dark:bg-black/80 backdrop-blur-md;
  }

  /* Animation utilities */
  .animate-in {
    animation: animateIn var(--animate-normal) ease-out;
  }

  @keyframes animateIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

/* Components */
@layer components {
  /* Base button styles */
  .btn {
    @apply inline-flex items-center justify-center font-medium rounded-md;
    @apply px-4 py-2 text-sm;
    @apply transition-all duration-[var(--animate-fast)];
    @apply focus:outline-none focus:ring-2 focus:ring-offset-2;
    @apply disabled:opacity-50 disabled:cursor-not-allowed;
  }

  /* Button variants - no longer using @apply btn */
  .btn-primary {
    @apply inline-flex items-center justify-center font-medium rounded-md;
    @apply px-4 py-2 text-sm;
    @apply transition-all duration-[var(--animate-fast)];
    @apply focus:outline-none focus:ring-2 focus:ring-offset-2;
    @apply disabled:opacity-50 disabled:cursor-not-allowed;
    @apply bg-primary-600 text-white hover:bg-primary-700;
    @apply focus:ring-primary-500;
  }

  .btn-secondary {
    @apply inline-flex items-center justify-center font-medium rounded-md;
    @apply px-4 py-2 text-sm;
    @apply transition-all duration-[var(--animate-fast)];
    @apply focus:outline-none focus:ring-2 focus:ring-offset-2;
    @apply disabled:opacity-50 disabled:cursor-not-allowed;
    @apply bg-secondary-100 text-secondary-900 hover:bg-secondary-200;
    @apply dark:bg-secondary-800 dark:text-secondary-100 dark:hover:bg-secondary-700;
    @apply focus:ring-secondary-500;
  }

  .btn-ghost {
    @apply inline-flex items-center justify-center font-medium rounded-md;
    @apply px-4 py-2 text-sm;
    @apply transition-all duration-[var(--animate-fast)];
    @apply focus:outline-none focus:ring-2 focus:ring-offset-2;
    @apply disabled:opacity-50 disabled:cursor-not-allowed;
    @apply hover:bg-secondary-100 dark:hover:bg-secondary-800;
    @apply focus:ring-secondary-500;
  }

  /* Card component */
  .card {
    @apply bg-white dark:bg-secondary-900;
    @apply border border-border;
    @apply rounded-lg shadow-sm;
    @apply p-6;
  }

  /* Input component */
  .input {
    @apply w-full px-3 py-2;
    @apply bg-background border border-border rounded-md;
    @apply focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent;
    @apply placeholder:text-muted;
    @apply transition-shadow duration-[var(--animate-fast)];
  }

  /* Container */
  .container {
    @apply w-full mx-auto px-4 sm:px-6 lg:px-8;
    @apply max-w-7xl;
  }
}`
}

export function postcssConfigTemplate(): string {
  return `/** @type {import('postcss').Config} */
module.exports = {
  plugins: {
    '@tailwindcss/postcss': {},
  },
};`
}
